import { Action } from 'redux';
import { ACTION_CREATOR_NAME, errorToBasketErrorModel, queryHelper, userHelper } from 'helpers';
import { AppState, ErrorAction } from 'store/root.types';
import { BasketActionTypes, BasketActions } from '..';
import { api } from 'repositories/api';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { delay, put, select } from 'redux-saga/effects';
import { addNotification } from 'store/notifications/actions';
import { AddToCartItemModel, NotificationTypes } from 'models';
import { loadBasketSaga } from './loadBasket';
import { BasketItemModel } from 'models/Basket';

export interface AddToCartFromUrlAction extends Action<BasketActionTypes.ADD_TO_CART_FROM_URL> {
    query: string;
}

export interface AddToCartFromUrlActionStart extends Action<BasketActionTypes.ADD_TO_CART_FROM_URL_START> {
}

export interface AddToCartFromUrlActionSuccess extends Action<BasketActionTypes.ADD_TO_CART_FROM_URL_SUCCESS> {
}

export interface AddToCartFromUrlActionError extends ErrorAction<BasketActionTypes.ADD_TO_CART_FROM_URL_ERROR> {
}

export const addToCartFromUrl: (query: string) => AddToCartFromUrlAction = (query: string) => ({
    type: BasketActionTypes.ADD_TO_CART_FROM_URL,
    query: query
});

addToCartFromUrl[ACTION_CREATOR_NAME] = 'addToCartFromUrl';

export type addToCartFromUrlFunc = (...args: Parameters<typeof addToCartFromUrl>) => void;

const PARTIAL_SUCCESS_WARNING_MESSAGE = 'Some items could not be added to your basket. Please try again or call 03444 81 81 81 to speak to a Sofologist.';
const FAILED_ADD_TO_CART_ERROR_MESSAGE = 'Item(s) could not be added to your basket. Please try again or call 03444 81 81 81 to speak to a Sofologist.';

export function* AddToBasketFromUrlSaga({ query }: AddToCartFromUrlAction): BasketSagaReturnType {
    yield put<BasketActions>({
        type: BasketActionTypes.ADD_TO_CART_FROM_URL_START
    });

    try {
        const queryItems = queryHelper.parseCartItemsFromQuery(query);

        if (!queryItems.length) {
            // Sometimes if there are no items state of isAddingToCartFromUrlProcessing does not change without delay
            yield delay(10);
            yield put<BasketActions>({
                type: BasketActionTypes.ADD_TO_CART_FROM_URL_SUCCESS
            });

            return true;
        }

        yield callBasketSaga(loadBasketSaga);

        const state: AppState = yield select();
        const basket = state.basketState?.basket;

        const itemsToAdd = filterItemsToAdd(queryItems, basket?.entries);

        if (!itemsToAdd.length) {
            yield put<BasketActions>({
                type: BasketActionTypes.ADD_TO_CART_FROM_URL_SUCCESS
            });

            return true;
        }

        const username = userHelper.getCurrentUserName();
        const basketGuid = basket?.guid ?? null;

        const batchUpdateResult = yield api.hybris.addProductsBatch(username, basketGuid, itemsToAdd);

        yield put<BasketActions>({ type: BasketActionTypes.ADD_TO_CART_FROM_URL_SUCCESS });

        if (batchUpdateResult.failed?.length) {
            yield put(addNotification(PARTIAL_SUCCESS_WARNING_MESSAGE, NotificationTypes.Err));
        }

        return true;
    } catch (error) {
        yield put({
            type: BasketActionTypes.ADD_TO_CART_FROM_URL_ERROR,
            error: errorToBasketErrorModel(error, 'addToCartFromUrlSaga')
        });

        yield put(addNotification(FAILED_ADD_TO_CART_ERROR_MESSAGE, NotificationTypes.Err));

        return false;
    }
}

function filterItemsToAdd(requestedItems: AddToCartItemModel[], currentBasketItems: BasketItemModel[] | undefined): AddToCartItemModel[] {
    if (!currentBasketItems?.length) {
        return requestedItems;
    }

    const currentQuantityMap = new Map<string, number>(
        currentBasketItems.map(item => [item.product.code, item.quantity])
    );

    const result: AddToCartItemModel[] = [];

    for (const item of requestedItems) {
        const currentQty = currentQuantityMap.get(item.productCode) ?? 0;

        if (item.quantity > currentQty) {
            result.push({
                productCode: item.productCode,
                quantity: item.quantity - currentQty
            });
        }
    }

    return result;
}